import Button from "@mui/material/Button";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { DialogHeader } from "../../../auth/components/PasswordRecoveryModal";
import { UsinasContext } from "../context/UsinasContextProvider";
import { useContext } from "react";
import { DivergentIndicatorsItem } from "./DivergentIndicatorsItem";
import {
  getDivergentSingleValuesIndicators,
  getDivergentMonthlyValues,
  checkDivergentSingleValues,
} from "../divergentIndicators";
import { Indicators } from "../types";
import { IndicatorsData } from "./PVSystForm";
import { MWhValueData } from "./MWhValueForm";

export function ConfirmIndicatorsChangesModal(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: ((data: IndicatorsData) => void) | ((data: MWhValueData) => void);
  newIndicators: Indicators;
  fromScreen: "PVSystForm" | "MWhValueForm";
  formData: IndicatorsData | MWhValueData;
}) {
  const { open, setOpen, onConfirm, newIndicators, fromScreen, formData } =
    props;
  const { currentIndicators } = useContext(UsinasContext);
  const onClose = () => setOpen(false);

  const divergentIndicators = getDivergentSingleValuesIndicators(
    currentIndicators,
    newIndicators
  );
  const divergentMegaWattValue =
    fromScreen == "MWhValueForm" &&
      checkDivergentSingleValues(
        currentIndicators?.megaWattValue,
        newIndicators.megaWattValue
      )
      ? [
        {
          label: "Valor do MWh",
          currentValue: currentIndicators?.megaWattValue,
          newValue: newIndicators.megaWattValue,
        },
      ]
      : [];

  const divergentPr = getDivergentMonthlyValues(
    currentIndicators?.pr,
    newIndicators.pr
  );
  const divergentIrradiance = getDivergentMonthlyValues(
    currentIndicators?.irradiance,
    newIndicators.irradiance
  );
  const divergentPredictedEnergy = getDivergentMonthlyValues(
    currentIndicators?.predictedEnergy,
    newIndicators.predictedEnergy
  );
  const divergentOpticalLoss = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedOpticalLoss,
    newIndicators.pvSystExpectedOpticalLoss
  );
  const divergentConversionLoss = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedConversionLoss,
    newIndicators.pvSystExpectedConversionLoss
  );
  const divergentInverterLoss = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedInverterLoss,
    newIndicators.pvSystExpectedInverterLoss
  );
  const divergentTransformerLoss = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedTransformerLoss,
    newIndicators.pvSystExpectedTransformerLoss
  );
  const divergentSystem = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedSystem,
    newIndicators.pvSystExpectedSystem
  );
  const divergentContractual = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedContractual,
    newIndicators.pvSystExpectedContractual
  );
  const divergentInverter = getDivergentMonthlyValues(
    currentIndicators?.pvSystExpectedInverter,
    newIndicators.pvSystExpectedInverter
  );
  const showTotalPower = divergentIndicators.totalPower ? true : false;
  const showModulesArea = divergentIndicators.modulesArea ? true : false;
  const showKwPeak = divergentIndicators.kwPeak ? true : false;
  const showVariance = divergentIndicators.variance ? true : false;
  const showEfficiency = divergentIndicators.efficiency ? true : false;
  const showMegaWattValue = divergentMegaWattValue.length > 0 ? true : false;
  const showPr = divergentPr.length > 0 ? true : false;
  const showIrradiance = divergentIrradiance.length > 0 ? true : false;
  const showPredictedEnergy =
    divergentPredictedEnergy.length > 0 ? true : false;
  const showOpticalLoss = divergentOpticalLoss.length > 0 ? true : false;
  const showConversionLoss = divergentConversionLoss.length > 0 ? true : false;
  const showInverterLoss = divergentInverterLoss.length > 0 ? true : false;
  const showTransformerLoss = divergentTransformerLoss.length > 0 ? true : false;
  const showSystem = divergentSystem.length > 0 ? true : false;
  const showContractual = divergentContractual.length > 0 ? true : false;
  const showInverter = divergentInverter.length > 0 ? true : false;

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth="xl"
    >
      <DialogHeader id="customized-dialog-title" onClose={onClose}>
        Modificação de variáveis de cálculo de indicadores
      </DialogHeader>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
        dividers
      >
        <Typography gutterBottom>
          Deseja confirmar a alteração das variáveis de cálculo de indicadores?
        </Typography>
        <div
          style={{
            overflowY: "auto",
          }}
        >
          <div className="w-100 d-flex flex-row  justify-content-around px-2">
            <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
              Dados Anteriores
            </Typography>
            <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
              Novos Dados
            </Typography>
          </div>
          {showTotalPower && (
            <div className="w-100 d-flex flex-row  justify-content-between">
              <DivergentIndicatorsItem
                label={"Potência Total"}
                value={currentIndicators?.totalPower}
              />
              <DivergentIndicatorsItem
                label={"Potência Total"}
                value={newIndicators.totalPower}
              />
            </div>
          )}
          {showModulesArea && (
            <div className="w-100 d-flex flex-row  justify-content-between">
              <DivergentIndicatorsItem
                label={"Área dos módulos"}
                value={currentIndicators?.modulesArea}
              />
              <DivergentIndicatorsItem
                label={"Área dos módulos"}
                value={newIndicators.modulesArea}
              />
            </div>
          )}
          {showKwPeak && (
            <div className="w-100 d-flex flex-row  justify-content-between">
              <DivergentIndicatorsItem
                label={"kWpico"}
                value={currentIndicators?.kwPeak}
              />
              <DivergentIndicatorsItem
                label={"kWpico"}
                value={newIndicators.kwPeak}
              />
            </div>
          )}
          {showVariance && (
            <div className="w-100 d-flex flex-row  justify-content-between">
              <DivergentIndicatorsItem
                label={"Variância"}
                value={currentIndicators?.variance}
              />
              <DivergentIndicatorsItem
                label={"Variância"}
                value={newIndicators.variance}
              />
            </div>
          )}
          {showEfficiency && (
            <div className="w-100 d-flex flex-row  justify-content-between">
              <DivergentIndicatorsItem
                label={"Eficiência"}
                value={currentIndicators?.efficiency}
              />
              <DivergentIndicatorsItem
                label={"Eficiência"}
                value={newIndicators.efficiency}
              />
            </div>
          )}
          {showMegaWattValue && (
            <div>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Valor do MWh"}
              </Typography>
              {divergentMegaWattValue.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </div>
          )}
          {showPr && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"PR"}
              </Typography>
              {divergentPr.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showIrradiance && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Irradiância"}
              </Typography>
              {divergentIrradiance.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showPredictedEnergy && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Energia Prevista"}
              </Typography>
              {divergentPredictedEnergy.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showOpticalLoss && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Perda Óptica Esperada"}
              </Typography>
              {divergentOpticalLoss.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showConversionLoss && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Perda de Conversão Esperada"}
              </Typography>
              {divergentConversionLoss.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showInverterLoss && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Perda do Inversor Esperada"}
              </Typography>
              {divergentInverterLoss.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showTransformerLoss && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Perda do Transformador Esperada"}
              </Typography>
              {divergentTransformerLoss.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showSystem && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Sistema Esperado"}
              </Typography>
              {divergentSystem.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showContractual && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Contratual Esperado"}
              </Typography>
              {divergentContractual.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
          {showInverter && (
            <>
              <Typography sx={{ fontWeight: "bold", marginTop: "10px" }}>
                {"Inversor Esperado"}
              </Typography>
              {divergentInverter.map((divergent, index) => (
                <div
                  key={index}
                  className="w-100 d-flex flex-row  justify-content-between"
                >
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.currentValue}
                  />
                  <DivergentIndicatorsItem
                    label={divergent.label}
                    value={divergent.newValue}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if ("nameUsina" in formData) onConfirm(formData);
          }}
        >
          Confirmar alteração
        </Button>
      </DialogActions>
    </Dialog>
  );
}
